import React, { useEffect, useState, useCallback } from "react";
import { TrendingUp, Loader2, ChevronLeft, ChevronRight, Info, Rocket, Check, Snowflake } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtINR = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

/**
 * Revenue Projection widget — shows what this month could earn if every
 * booking slot (per the capacity rules) sold out, vs. what's been booked
 * so far. Makes weekend vs weekday capacity decisions money-driven.
 *
 * Read-only — any admin/manager role can view.
 */
const RevenueProjectionWidget = ({ authAxios }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/bookings/capacity/revenue-projection`, {
        params: { year, month },
      });
      setData(r.data);
    } catch { setData(null); }
    setLoading(false);
  }, [authAxios, year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- Boost helpers: group contiguous hot upcoming days into range clusters ---
  const HOT_THRESHOLD = 80;  // % utilization
  const BOOST_PCT = 20;      // bump %

  // --- Cool Down helpers: group contiguous cold upcoming days ---
  const COLD_THRESHOLD = 20;   // % utilization (<=)
  const COOLDOWN_PCT = 50;     // reduce cap by 50%
  const MIN_CLUSTER_DAYS = 2;  // skip single-day cold spots (too noisy)
  const MIN_CAP_FOR_COOLDOWN = 4;  // don't propose cooldown on caps already very low

  const hotClusters = React.useMemo(() => {
    if (!data?.days) return [];
    const clusters = [];
    let current = null;
    for (const d of data.days) {
      const hot = !d.in_past && d.capacity > 0 && d.utilization_pct >= HOT_THRESHOLD;
      if (hot) {
        if (!current) {
          current = { start: d.date, end: d.date, maxCap: d.capacity, dates: [d.date] };
        } else {
          current.end = d.date;
          current.maxCap = Math.max(current.maxCap, d.capacity);
          current.dates.push(d.date);
        }
      } else if (current) {
        clusters.push(current);
        current = null;
      }
    }
    if (current) clusters.push(current);
    // Propose boosted cap (ceil by 20% of max cap in the cluster, min +1 slot)
    return clusters.map((c) => ({
      ...c,
      proposed_cap: Math.max(c.maxCap + 1, Math.ceil(c.maxCap * (1 + BOOST_PCT / 100))),
      added_slots: Math.max(c.maxCap + 1, Math.ceil(c.maxCap * (1 + BOOST_PCT / 100))) - c.maxCap,
    }));
  }, [data]);

  const coldClusters = React.useMemo(() => {
    if (!data?.days) return [];
    const clusters = [];
    let current = null;
    for (const d of data.days) {
      const cold = !d.in_past && d.capacity >= MIN_CAP_FOR_COOLDOWN && d.utilization_pct <= COLD_THRESHOLD;
      if (cold) {
        if (!current) {
          current = { start: d.date, end: d.date, maxCap: d.capacity, minCap: d.capacity, dates: [d.date] };
        } else {
          current.end = d.date;
          current.maxCap = Math.max(current.maxCap, d.capacity);
          current.minCap = Math.min(current.minCap, d.capacity);
          current.dates.push(d.date);
        }
      } else if (current) {
        clusters.push(current);
        current = null;
      }
    }
    if (current) clusters.push(current);
    // Propose cooldown cap (floor by -50% of the min cap, clamped to min 1)
    return clusters
      .filter((c) => c.dates.length >= MIN_CLUSTER_DAYS)
      .map((c) => {
        const proposed = Math.max(1, Math.floor(c.minCap * (1 - COOLDOWN_PCT / 100)));
        return {
          ...c,
          proposed_cap: proposed,
          removed_slots: c.minCap - proposed,
        };
      });
  }, [data]);

  const [boostingId, setBoostingId] = useState(null);
  const [boostedKeys, setBoostedKeys] = useState(new Set());
  const [coolingId, setCoolingId] = useState(null);
  const [cooledKeys, setCooledKeys] = useState(new Set());

  const applyBoost = async (cluster) => {
    const key = `${cluster.start}__${cluster.end}`;
    if (boostedKeys.has(key) || !cluster || cluster.dates.length === 0) return;
    const ok = window.confirm(
      `Boost ${cluster.dates.length} day(s) from ${cluster.start} → ${cluster.end} to ${cluster.proposed_cap} slots/day (+${BOOST_PCT}%)?`
    );
    if (!ok) return;
    setBoostingId(key);
    try {
      await authAxios.post(`${API_URL}/api/admin/bookings/capacity/range-rules`, {
        date_from: cluster.start,
        date_to: cluster.end,
        limit: cluster.proposed_cap,
        label: `Boost · +${BOOST_PCT}% (${cluster.dates.length}d high demand)`,
      });
      setBoostedKeys((s) => new Set(s).add(key));
      await fetchData();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to apply boost");
    }
    setBoostingId(null);
  };

  const applyCooldown = async (cluster) => {
    const key = `${cluster.start}__${cluster.end}`;
    if (cooledKeys.has(key) || !cluster || cluster.dates.length === 0) return;
    const ok = window.confirm(
      `Cool down ${cluster.dates.length} day(s) from ${cluster.start} → ${cluster.end}? ` +
      `Cap will drop from ${cluster.minCap} → ${cluster.proposed_cap} slots/day (-${COOLDOWN_PCT}%).`
    );
    if (!ok) return;
    setCoolingId(key);
    try {
      await authAxios.post(`${API_URL}/api/admin/bookings/capacity/range-rules`, {
        date_from: cluster.start,
        date_to: cluster.end,
        limit: cluster.proposed_cap,
        label: `Cool Down · -${COOLDOWN_PCT}% (${cluster.dates.length}d low demand)`,
      });
      setCooledKeys((s) => new Set(s).add(key));
      await fetchData();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to apply cool down");
    }
    setCoolingId(null);
  };

  const prevMonth = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); } else { setMonth(month - 1); }
  };
  const nextMonth = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); } else { setMonth(month + 1); }
  };

  return (
    <div className="admin-card admin-card-hover p-5 mb-4" data-testid="revenue-projection-widget">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#10B981] to-[#F59E0B] flex items-center justify-center">
          <TrendingUp size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-[#1E1B4B] text-sm">Revenue projection</h3>
          <p className="text-xs text-[#94a3b8]">
            {data ? `Daily caps × avg booking value = ceiling. "At risk" is the gap you'd close if you sold out.` : "Loading…"}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="rev-prev-month">
            <ChevronLeft size={14} className="text-gray-500" />
          </button>
          <span className="text-xs font-mono font-bold text-[#1E1B4B] min-w-[72px] text-center" data-testid="rev-period">
            {year}-{String(month).padStart(2, "0")}
          </span>
          <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="rev-next-month">
            <ChevronRight size={14} className="text-gray-500" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-[#DC283C]" /></div>
      ) : !data ? (
        <p className="text-xs text-[#94a3b8] text-center py-4">No data.</p>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <Tile label="Ceiling" value={fmtINR(data.ceiling_revenue)} color="emerald" testid="rev-ceiling" />
            <Tile label="Booked" value={fmtINR(data.booked_revenue)} color="indigo" testid="rev-booked" />
            <Tile label="At risk" value={fmtINR(data.revenue_at_risk)} color="amber" testid="rev-at-risk" />
            <Tile label="Utilization" value={`${data.utilization_pct}%`} color="pink" testid="rev-util" />
          </div>

          <div className="text-[11px] text-[#94a3b8] bg-gray-50 rounded-lg px-2 py-1.5 mb-3 flex items-center gap-1">
            <Info size={11} /> Avg booking value: <span className="font-bold text-[#1E1B4B] mx-1">{fmtINR(data.avg_booking_value)}</span>
            <span className="italic">({data.avg_source})</span>
            <span className="ml-auto">{data.total_capacity_slots} slots · {data.booked_slots} booked</span>
          </div>

          {/* One-click "Boost" — proposes a +20% capacity bump for hot upcoming date clusters */}
          {hotClusters.length > 0 && (
            <div className="rounded-xl border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-3 mb-3" data-testid="boost-banner">
              <div className="flex items-start gap-2 mb-2">
                <Rocket size={16} className="text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-bold text-[#1E1B4B]">
                    {hotClusters.length === 1 ? "1 upcoming day cluster is" : `${hotClusters.length} upcoming day clusters are`} above {HOT_THRESHOLD}% utilization.
                  </p>
                  <p className="text-[10px] text-amber-700">
                    Proposing a +{BOOST_PCT}% capacity bump as a one-click range rule — captures demand without editing code.
                  </p>
                </div>
              </div>
              <div className="space-y-1.5">
                {hotClusters.map((c) => {
                  const key = `${c.start}__${c.end}`;
                  const done = boostedKeys.has(key);
                  return (
                    <div key={key} className="flex items-center gap-2 bg-white/70 rounded-lg px-2 py-1.5" data-testid={`boost-cluster-${c.start}`}>
                      <span className="text-[11px] font-mono text-gray-600">
                        {c.start === c.end ? c.start : `${c.start} → ${c.end}`}
                      </span>
                      <span className="text-[11px] text-gray-500">
                        {c.dates.length}d · cap {c.maxCap}
                      </span>
                      <span className="text-[11px] font-bold text-emerald-600">
                        → {c.proposed_cap} (+{c.added_slots})
                      </span>
                      <span className="flex-1" />
                      {done ? (
                        <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-700" data-testid={`boost-done-${c.start}`}>
                          <Check size={12} /> Applied
                        </span>
                      ) : (
                        <button
                          onClick={() => applyBoost(c)}
                          disabled={boostingId === key}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#DC283C] text-white text-[10px] font-bold hover:bg-[#c22d78] disabled:opacity-50"
                          data-testid={`boost-apply-${c.start}`}
                        >
                          {boostingId === key ? <Loader2 size={10} className="animate-spin" /> : <Rocket size={10} />}
                          Boost
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* One-click "Cool Down" — proposes a capacity reduction for cold upcoming clusters */}
          {coldClusters.length > 0 && (
            <div className="rounded-xl border-2 border-sky-200 bg-gradient-to-r from-sky-50 to-indigo-50 p-3 mb-3" data-testid="cooldown-banner">
              <div className="flex items-start gap-2 mb-2">
                <Snowflake size={16} className="text-sky-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-bold text-[#1E1B4B]">
                    {coldClusters.length === 1 ? "1 upcoming day cluster is" : `${coldClusters.length} upcoming day clusters are`} under {COLD_THRESHOLD}% utilization.
                  </p>
                  <p className="text-[10px] text-sky-700">
                    Proposing a -{COOLDOWN_PCT}% capacity cut — frees up staff to concentrate on high-demand days.
                  </p>
                </div>
              </div>
              <div className="space-y-1.5">
                {coldClusters.map((c) => {
                  const key = `${c.start}__${c.end}`;
                  const done = cooledKeys.has(key);
                  return (
                    <div key={key} className="flex items-center gap-2 bg-white/70 rounded-lg px-2 py-1.5" data-testid={`cooldown-cluster-${c.start}`}>
                      <span className="text-[11px] font-mono text-gray-600">
                        {c.start === c.end ? c.start : `${c.start} → ${c.end}`}
                      </span>
                      <span className="text-[11px] text-gray-500">
                        {c.dates.length}d · cap {c.minCap}
                      </span>
                      <span className="text-[11px] font-bold text-sky-600">
                        → {c.proposed_cap} (-{c.removed_slots})
                      </span>
                      <span className="flex-1" />
                      {done ? (
                        <span className="flex items-center gap-1 text-[11px] font-bold text-sky-700" data-testid={`cooldown-done-${c.start}`}>
                          <Check size={12} /> Applied
                        </span>
                      ) : (
                        <button
                          onClick={() => applyCooldown(c)}
                          disabled={coolingId === key}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-sky-600 text-white text-[10px] font-bold hover:bg-sky-700 disabled:opacity-50"
                          data-testid={`cooldown-apply-${c.start}`}
                        >
                          {coolingId === key ? <Loader2 size={10} className="animate-spin" /> : <Snowflake size={10} />}
                          Cool down
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Daily sparkline */}
          <div className="mb-3">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">Daily capacity vs bookings</p>
            <div className="flex items-end gap-0.5 h-16">
              {data.days.map((d) => {
                const pct = d.capacity > 0 ? Math.min(100, (d.booked / d.capacity) * 100) : 0;
                const capHeight = data.days.reduce((m, x) => Math.max(m, x.capacity), 1);
                const barH = (d.capacity / capHeight) * 100;
                return (
                  <div key={d.date} className="flex-1 flex flex-col justify-end" title={`${d.date} · ${d.booked}/${d.capacity} = ${d.utilization_pct}% · ${fmtINR(d.booked_revenue)} / ${fmtINR(d.ceiling_revenue)}`}>
                    <div className="relative w-full bg-gray-100 rounded-sm" style={{ height: `${barH}%` }}>
                      <div
                        className={`absolute bottom-0 left-0 right-0 rounded-sm ${pct >= 80 ? "bg-emerald-500" : pct >= 40 ? "bg-indigo-500" : "bg-pink-400"}`}
                        style={{ height: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[9px] text-gray-400 mt-1">
              <span>1</span><span>{Math.ceil(data.total_days / 2)}</span><span>{data.total_days}</span>
            </div>
          </div>

          {/* Weekday breakdown */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">By weekday</p>
            <div className="grid grid-cols-7 gap-1 text-center text-[11px]">
              {data.weekday_breakdown.map((w) => (
                <div key={w.idx} className="rounded-lg bg-gray-50 px-1 py-1.5" data-testid={`rev-weekday-${w.idx}`}>
                  <p className="text-[10px] font-bold text-gray-500">{w.weekday}</p>
                  <p className="font-bold text-[#1E1B4B]">{w.utilization_pct}%</p>
                  <p className="text-[9px] text-gray-400">{w.total_booked}/{w.total_capacity}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Tile = ({ label, value, color, testid }) => {
  const colors = {
    emerald: "text-emerald-700 before:bg-emerald-400",
    indigo: "text-indigo-700 before:bg-indigo-400",
    amber: "text-amber-700 before:bg-amber-400",
    pink: "text-[#DC283C] before:bg-[#DC283C]",
  }[color];
  return (
    <div className={`admin-stat px-3.5 py-3 relative before:content-[''] before:absolute before:left-0 before:top-3 before:bottom-3 before:w-1 before:rounded-full ${colors}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 pl-2">{label}</p>
      <p className={`text-lg font-extrabold pl-2 ${colors}`}>{value}</p>
    </div>
  );
};

export default RevenueProjectionWidget;
